import React, { useState, useEffect, useRef } from "react";
import { Card, Avatar, Input, Button } from "antd";
import { SendOutlined, UserOutlined, LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useDispatch, useSelector } from "react-redux";
import { appDispatch, RootState } from "../../../app/store";
import { StorageService } from "../../../core/storageService/storageService";
import { getMessages, getPrivateKey, getPublicKey, getSuperAdminDetails, sendEncryptedMessage } from "../../../features/members/membersSlice";
import { toast } from 'react-toastify';

const HEADER_HEIGHT = 48;

interface Message {
  text?: string;
  encrypted_message?: string;
  encrypted_key?: string;
  iv?: string;
  fromMe: boolean;
  id?: string;
  created_at?: string;
  receiver_id:any;
  sender_id:any;
  sender_encrypted_key:any;
}

const BACommunicationModule = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<appDispatch>();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const apiMessages = useSelector((state: RootState) => state.members.getMessages.getMessagesData);
  const superAdminId = useSelector((state: RootState) => state.members.getSuperAdminDetails.getSuperAdminDetailsData);

  const [message, setMessage] = useState("");
  const [publicKeyPem, setPublicKeyPem] = useState<string | null>(null);
  const [receiverPublicKeyPem, setReceiverPublicKeyPem] = useState<any | null>(null);

  const [decryptedMessages, setDecryptedMessages] = useState<any[]>([]);

  const storedData = StorageService.getObjStorage("super_admin_user");
  const userObj = storedData ? (typeof storedData === "string" ? JSON.parse(storedData) : storedData) : null;
  const userId = userObj?.result?.user_data?.id || "";

  // ---------------- Scroll to bottom ----------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [decryptedMessages]);

  // ---------------- Load Public & Private Keys ----------------
  useEffect(() => {
    const loadKeys = async () => {
      try {
        const pubRes =  localStorage.getItem("public_key");
        setPublicKeyPem(pubRes);
      } catch (err) {
        console.error("Failed to load keys:", err);
      }
    };
    loadKeys();
  }, [dispatch, userId]);

  // ---------------- Fetch Messages ----------------
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        await dispatch(getMessages({ id: superAdminId.id }));
      } catch (err) {
        console.error("Failed to fetch messages:", err);
      }
    };
    fetchMessages();
  }, [dispatch, userId]);


  useEffect(()=>{
 
    const fetchSADetails = async () => {
      try {
        await dispatch(getSuperAdminDetails({  }));
      } catch (err) {
        console.error("Failed to fetch messages:", err);
      }
    };
    fetchSADetails();
  },[])

useEffect(() => {
  if (!superAdminId?.id) return;

  const fetchPublicKeyDetails = async () => {
    const res = await dispatch(
      getPublicKey({ id: superAdminId.id })
    ).unwrap();

    setReceiverPublicKeyPem(res.result.public_key);
  };

  fetchPublicKeyDetails();
}, [superAdminId]);

  /* ===================== WEB CRYPTO HELPERS (AES-GCM) ===================== */
  
  const ab2b64 = (buf: ArrayBuffer) =>
    btoa(String.fromCharCode(...new Uint8Array(buf)));

  const b642ab = (b64: string) =>
    Uint8Array.from(atob(b64), c => c.charCodeAt(0)).buffer;

  const generateAESKey = () =>
    crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );

  const encryptMessageAES = async (plaintext: string, key: CryptoKey) => {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(plaintext)
    );
    return {
      ciphertext: ab2b64(encrypted),
      iv: ab2b64(iv.buffer as ArrayBuffer),
    };
  };

  const decryptMessageAES = async (
    ciphertext: string,
    key: CryptoKey,
    ivBase64: string
  ) => {
    const iv = b642ab(ivBase64);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(iv) },
      key,
      b642ab(ciphertext)
    );
    return new TextDecoder().decode(decrypted);
  };

  const importPrivateKey = async (pem: string) => {
    const base64 = pem.replace(/-----.*KEY-----/g, "").replace(/[\r\n\s]/g, "");
    const buffer = b642ab(base64);
    return crypto.subtle.importKey(
      "pkcs8",
      buffer,
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["decrypt"]
    );
  };

const pemToArrayBuffer = (pem: string): ArrayBuffer => {
  // 1. Extract the body content using a regex that finds the headers
  const pattern = /-----BEGIN [^-]+-----([\s\S]+?)-----END [^-]+-----/;
  const match = pem.match(pattern);
  
  let content = "";
  if (match) {
    content = match[1];
  } else {
    content = pem
      .replace(/-----BEGIN [^-]+-----/, "")
      .replace(/-----END [^-]+-----/, "");
  }

  // 2. Fix common corruption: " " (space) instead of "+"
  content = content.replace(/ /g, "+");

  // 3. Remove all non-base64 characters
  const base64 = content.replace(/[^A-Za-z0-9+/=]/g, "");
  return b642ab(base64);
};

const importPublicKey = (pem: string) =>
  crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(pem),
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );

const encryptAESKeyWithRSA = async (aesKey: CryptoKey, publicKeyPem: string) => {
  const normalizedKey = normalizePublicKey(publicKeyPem);
  const publicKey = await importPublicKey(normalizedKey);
  
  // Export the AES key to raw bytes
  const rawKey = await crypto.subtle.exportKey("raw", aesKey);

  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    rawKey
  );
  return ab2b64(encrypted);
};

const decryptAESKeyWithRSA = async (
  encryptedKeyBase64: string,
  privateKey: CryptoKey
): Promise<CryptoKey> => {
  const encryptedBytes = b642ab(encryptedKeyBase64);
  const decryptedRawKey = await crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKey,
    encryptedBytes
  );
  
  return crypto.subtle.importKey(
    "raw",
    decryptedRawKey,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"] // Need both if we want to reply using same key, though usually we regen
  );
};

const decryptChatMessage = async (
  msg: Message,
  privateKeyPem: string,
  loggedInUserId: number
) => {
  if (!privateKeyPem) return "[Missing private key]";

  const privateKey = await importPrivateKey(privateKeyPem);

  let encryptedAesKey: string | undefined;

  // RECEIVED
  if (
    Number(msg.receiver_id) === loggedInUserId &&
    msg.encrypted_key
  ) {
    encryptedAesKey = msg.encrypted_key;
  }
  // SENT
  else if (
    Number(msg.sender_id) === loggedInUserId &&
    msg.sender_encrypted_key
  ) {
    encryptedAesKey = msg.sender_encrypted_key;
  }

  if (!encryptedAesKey) {
    return "[Key unavailable]";
  }

  try {
    const aesKey = await decryptAESKeyWithRSA(
      encryptedAesKey,
      privateKey
    );

    return await decryptMessageAES(
      msg.encrypted_message!,
      aesKey,
      msg.iv!
    );
  } catch (err) {
    console.error("Message decryption failed", err);
    return "[Unable to decrypt]";
  }
};

const normalizePublicKey = (key: string) => {
  if (!key) throw new Error("Missing public key");
  key = key.replace(/\\n/g, "\n").trim();
  if (!key.includes("BEGIN PUBLIC KEY")) {
    throw new Error("Public key is not SPKI format");
  }
  return key;
};

/* ------------------------------------------------------------- */

  // ---------------- Decrypt API Messages ----------------
  useEffect(() => {
    const decryptAllMessages = async () => {
      const privateKeyPem = localStorage.getItem("private_key");
      if (!privateKeyPem || !apiMessages) return;

      const decrypted: Message[] = await Promise.all(
        apiMessages.map(async (msg: any) => {
          const text = await decryptChatMessage(msg, privateKeyPem, userId);

          return {
            ...msg,
            text,
            fromMe: Number(msg.sender_id) === userId,
          };
        })
      );
      setDecryptedMessages(decrypted);
    };
    decryptAllMessages();
  }, [apiMessages, userId]);


  // ---------------- Send Message ----------------
const handleSend = async () => {
 
  if (!message.trim() || !publicKeyPem || !receiverPublicKeyPem) return;

  const text = message;
  
  // 1. Generate AES-GCM Key (CryptoKey)
  const aesKey = await generateAESKey();
  
  // 2. Encrypt text
  const { ciphertext, iv } = await encryptMessageAES(text, aesKey);

  // 3. Encrypt AES Key for Receiver
  const encrypted_key = await encryptAESKeyWithRSA(
    aesKey,
    receiverPublicKeyPem
  );
  
  // 4. Encrypt AES Key for Sender (Me)
  const sender_encrypted_key = await encryptAESKeyWithRSA(
    aesKey,
    publicKeyPem
  );

  const payload = {
    encrypted_message: ciphertext, // Now Base64 from AES-GCM
    encrypted_key,
    sender_encrypted_key,
    iv,                            // Now Base64 from AES-GCM IV
    receiver_id: superAdminId.id,
  };

  await dispatch(sendEncryptedMessage(payload)).unwrap();

  setDecryptedMessages((prev) => [
    ...prev,
    { text, fromMe: true, created_at: new Date().toISOString() },
  ]);

  setMessage("");
};

  // ---------------- Render ----------------
  return (
    <div style={{ width: "100vw", height: "100%", display: "flex", flexDirection: "column", background: "#11161c", overflow: "hidden" }}>
      <div style={{ height: HEADER_HEIGHT, display: "flex", alignItems: "center", padding: "0 16px", background: "#21262c", borderBottom: "1px solid #21262c" }}>
        <Button type="text" icon={<LeftOutlined />} onClick={() => navigate(-1)} style={{ color: "#fff" }} />
        <span style={{ color: "#fff", fontWeight: 500, marginLeft: 8 }}>Chat with Super Admin</span>
      </div>

      <Card style={{ borderRadius: 0 }} bodyStyle={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, background: "#21262c" }}>
        <Avatar size={40} icon={<UserOutlined />} />
        <div>
          <div style={{ color: "#fff", fontWeight: 500 }}>Super Admin</div>
          <div style={{ fontSize: 12, color: "#1a865f" }}>● Online</div>
        </div>
      </Card>

      <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
        {decryptedMessages.length === 0 && <div style={{ textAlign: "center", color: "#777b80", marginTop: 40 }}>Start a conversation 👋</div>}
        {decryptedMessages.map((msg, idx) => (
          <div key={idx} style={{ display: "flex", justifyContent: msg.fromMe ? "flex-end" : "flex-start", marginBottom: 8 }}>
            <div style={{
              background: msg.fromMe ? "#4554fa" : "#21262c",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: msg.fromMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              maxWidth: "65%",
              fontSize: 14,
              boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
            }}>
              {msg.text}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ padding: "12px", borderTop: "1px solid #21262c", background: "#21262c" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Type a message..." onPressEnter={handleSend} style={{ borderRadius: 20 }} />
          <Button type="primary" shape="circle" icon={<SendOutlined />} onClick={handleSend} />
        </div>
      </div>
    </div>
  );
};

export default BACommunicationModule;